import {Sprite} from 'pixi.js';
import {Rectangle, ScreenParameters} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {StickyRichLabelData, StickyRichTextLabelComponent, WaterMarkLabels, WaterMarkViewInitData} from '@gongxigames/framework/slot/modules/watermark';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export abstract class WaterMarkView extends View<GlobalEventsMapForBackwardCompatibility> {

    public static NAME = Symbol('WaterMarkView');
    protected GLOBAL_CLOCK_OFFSET = 0;
    protected GLOBAL_CLOCK_INITIAL_OFFSET = 0;
    protected imageSprite?: Sprite;
    protected isGlobalClockEnabled = false;
    protected isNetPositionEnabled = false;
    protected isOffsetAdded = false;
    protected waterMarkViewInitData?: WaterMarkViewInitData;
    protected _isFullscreen = false;

    protected abstract globalTimeClockLabelData: StickyRichLabelData;
    protected abstract elapsedTimeClockLabelData: StickyRichLabelData;
    protected abstract sessionBalanceLabelData: StickyRichLabelData;
    protected abstract globalTimeClockPureData: StickyRichLabelData;
    protected abstract gameNameLabelData: StickyRichLabelData;
    protected abstract sessionEarningsLabelData: StickyRichLabelData;
    protected abstract sessionBetsLabelData: StickyRichLabelData;
    protected abstract maxWinProbabilityLabelData: StickyRichLabelData;
    protected abstract RTPWatermarkLabelData: StickyRichLabelData;

    protected globalTimeClock?: StickyRichTextLabelComponent;
    protected elapsedTimeClock?: StickyRichTextLabelComponent;
    protected sessionBalance?: StickyRichTextLabelComponent;
    protected globalTimeClockPure?: StickyRichTextLabelComponent;
    protected gameNameLabel?: StickyRichTextLabelComponent;
    protected sessionEarningsLabel?: StickyRichTextLabelComponent;
    protected sessionBetsLabel?: StickyRichTextLabelComponent;
    protected maxWinProbabilityLabel?: StickyRichTextLabelComponent;
    protected RTPWatermarkLabel?: StickyRichTextLabelComponent;

    protected labels?: WaterMarkLabels;

    public init(): void {
        super.init();

        this.initMainSprite();
        this.createFactory();
        this.initGraphicElements();
        this.hide();
    }

    public updateGlobalClock(globalTime: string): void {
        if (this.isGlobalClockEnabled) {
            this.globalTimeClock?.updateText(globalTime);
            this.globalTimeClockPure?.updateText(globalTime);
        }
    }

    public updateElapsedTimeClock(elapsedTime: string): void {
        this.elapsedTimeClock?.updateText(elapsedTime);
    }

    public updateSessionBalance(value: string): void {
        this.sessionBalance?.updateText(value);
    }

    public updateMaxWinProbability(value: string): void {
        this.maxWinProbabilityLabel?.updateText(value);
    }

    public updateRTPWatermark(value: string): void {
        this.RTPWatermarkLabel?.updateText(value);
    }

    public switchOnGlobalClock(): void {
        this.isGlobalClockEnabled = true;
        this.globalTimeClock?.show();
    }

    public switchOnNetPosition(): void {
        this.isNetPositionEnabled = true;
        this.sessionBalance?.show();
    }

    public setMenuBarOffsets(isAddOffset: boolean): void {
        this.isOffsetAdded = isAddOffset;
    }

    public onResized(screenParams: ScreenParameters): void {
        super.onResized(screenParams);

        this.labels?.resize(screenParams, this._isFullscreen);
    }

    public initProperties(
        data: WaterMarkViewInitData
    ): void {
        this.waterMarkViewInitData = data;

        if (data.isGlobalClockEnabled) {
            this.switchOnGlobalClock();
        }

        if (data.isNetPositionEnabled) {
            this.switchOnNetPosition();
        }

        if (data.isElapsedTimeEnabled) {
            this.switchOnElapsedTime();
        }

        if (data.isSessionEarningsEnabled) {
            this.switchOnSessionEarnings();
        }

        if (data.isSessionBetsEnabled) {
            this.switchOnSessionBets();
        }

        if (data.isShowMaxWinProbabilityEnabled) {
            this.switchOnMaxWinProbability();
        }

        if (data.isRTPWatermarkEnabled) {
            this.switchOnRTPWatermark();
        }

        this.gameNameLabel?.updateText(data.gameName);
    }

    public set isFullscreen(value: boolean) {
        this._isFullscreen = value;
    }

    public updateSessionBet(value: string): void {
        this.sessionBetsLabel?.updateText(value);
    }

    public updateSessionEarnings(value: string): void {
        this.sessionEarningsLabel?.updateText(value);
    }

    public updateBalanceFieldBounds(bounds: Rectangle): void {}

    protected switchOnElapsedTime(): void {
        this.elapsedTimeClock?.show();

        if (this.isGlobalClockEnabled && this.elapsedTimeClock?.sourceContainer && this.elapsedTimeClock.initialPosition) {
            this.elapsedTimeClock.sourceContainer.y += this.GLOBAL_CLOCK_OFFSET;
            this.elapsedTimeClock.initialPosition.y += this.GLOBAL_CLOCK_INITIAL_OFFSET;
        }
    }

    protected switchOnSessionEarnings(): void {
        this.sessionEarningsLabel?.show();
    }

    protected switchOnSessionBets(): void {
        this.sessionBetsLabel?.show();
    }

    protected switchOnMaxWinProbability(): void {
        this.maxWinProbabilityLabel?.show();
    }

    protected switchOnRTPWatermark(): void {
        this.RTPWatermarkLabel?.show();
    }

    protected initMainSprite(): void {
    }

    protected createFactory(): void {
        if (this.imageSprite) {
            this.labels = new WaterMarkLabels(this.imageSprite, this.objectBuilder);
        }
    }

    protected initGraphicElements(): void {
        this.initElapsedTimeClock();
        this.initSessionBalance();
        this.initGlobalTimeClock();
        this.initGameName();
        this.initSessionEarnings();
        this.initSessionBets();
        this.initRTPWatermark();
        this.initMaxWinProbability();
    }

    protected initElapsedTimeClock(): void {
        this.elapsedTimeClock = this.labels?.addRichLabel(this.elapsedTimeClockLabelData);
        this.elapsedTimeClock?.hide();
    }

    protected initSessionBalance(): void {
        this.sessionBalance = this.labels?.addRichLabel(this.sessionBalanceLabelData);
        this.sessionBalance?.hide();
    }

    protected initGlobalTimeClock(): void {
        this.globalTimeClock = this.labels?.addRichLabel(this.globalTimeClockLabelData);
        this.globalTimeClock?.hide();
    }

    protected initGameName(): void {
        this.gameNameLabel = this.labels?.addRichLabel(this.gameNameLabelData);
    }
    protected initSessionEarnings(): void {
        this.sessionEarningsLabel = this.labels?.addRichLabel(this.sessionEarningsLabelData);
        this.sessionEarningsLabel?.hide();
    }
    protected initSessionBets(): void {
        this.sessionBetsLabel = this.labels?.addRichLabel(this.sessionBetsLabelData);
        this.sessionBetsLabel?.hide();
    }

    protected initMaxWinProbability(): void {
        this.maxWinProbabilityLabel = this.labels?.addRichLabel(this.maxWinProbabilityLabelData);
        this.maxWinProbabilityLabel?.hide();
    }

    protected initRTPWatermark(): void {
        this.RTPWatermarkLabel = this.labels?.addRichLabel(this.RTPWatermarkLabelData);
        this.RTPWatermarkLabel?.hide();
    }
}
